<script setup lang="ts">
import { computed, type PropType, ref, useAttrs, watch, watchEffect } from 'vue'
import { useTranslate } from '@/shared/lib'
import { ru } from 'date-fns/locale'
import { VIcons } from '@/shared/ui'
import { useMandatoryFieldError } from '../../model/useMandatoryFieldError'
import '../assets/style.scss'

defineOptions({ inheritAttrs: false })

const attrs = useAttrs()

const props = defineProps({
  type: {
    type: String as PropType<'date' | 'range' | 'period' | 'month' | 'year'>,
    default: 'date',
  },
  hideDetails: {
    type: Boolean,
    default: true,
  },
  label: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  max: {
    type: Object as PropType<Boolean | String>,
    default: false,
  },
  min: {
    type: Object as PropType<Boolean | String>,
    default: false,
  },
  mandatory: {
    default: false,
  },
  showMandatoryError: {
    type: Boolean,
    default: false,
  },
})
/** Вычисляем значение для multiple **/
const multipleValue = computed<string | boolean>(() => {
  if (props.type == 'range') return 'range'

  return false
})

const value = defineModel<string | string[]>({ default: '' })
const { trs } = useTranslate()
const {
  visible: mandatoryErrorVisible,
  markTouched,
  registerUserInteraction,
} = useMandatoryFieldError(
  () => ({
    showMandatoryError: props.showMandatoryError,
    mandatory: props.mandatory,
    type: props.type,
  }),
  () => value.value,
)

function onDatePickerClosed() {
  isPickerOpen.value = false
  markTouched()
}

/** Format hint shown as placeholder when the input is empty. */
const dateInputPlaceholder = computed(() => {
  switch (props.type) {
    case 'month':
      return 'ММ.ГГГГ'
    case 'year':
      return 'ГГГГ'
    default:
      return trs('itf.date_placeholder_dmy')
  }
})

/** Список периодов */
const periodsList = {
  yesterday: {
    value: 'yesterday',
    title: trs('itf.periods.yesterday'),
    rangeValue: -1,
  },
  tomorrow: {
    value: 'tomorrow',
    title: trs('itf.periods.tomorrow'),
    rangeValue: 1,
  },
  previous_week: {
    value: 'previous_week',
    title: trs('itf.periods.previous_week'),
    rangeValue: -7,
  },
  next_week: {
    value: 'next_week',
    title: trs('itf.periods.next_week'),
    rangeValue: 7,
  },
  previous_month: {
    value: 'previous_month',
    title: trs('itf.periods.previous_month'),
    rangeValue: -30,
  },
  next_month: {
    value: 'next_month',
    title: trs('itf.periods.next_month'),
    rangeValue: 30,
  },
}

const currentPeriod = ref('yesterday')
const currentValue = ref<string | string[]>('')
const pickerRef = ref<any>(null)

watchEffect(() => {
  console.log(currentValue.value)
})

/** VueDatePicker leaves `dp__input_focus` on the input after blur, so CSS :focus is
 * unreliable for the caption/border color. Track the picker's open state explicitly. */
const isPickerOpen = ref(false)

/** VueDatePicker attrs — skip field metadata props from InventTypeField. */
const pickerAttrs = computed(() => {
  const { field, fieldData, class: _class, ...rest } = attrs
  return rest
})

const wrapperClass = computed(() => attrs.class)

/**
 * When the picker opens with no value, pre-set the current year/month
 * so the calendar view doesn't show a blank or 1900-era screen.
 */
const handleOpen = () => {
  isPickerOpen.value = true
  registerUserInteraction()
  if (currentValue.value || !pickerRef.value) return
  const now = new Date()
  pickerRef.value.setMonthYear?.({ month: now.getMonth(), year: now.getFullYear() })
}

/** Наблюдаем за значение и вычисляем дату **/
watch(
  value,
  () => {
    currentValue.value = value.value
  },
  { immediate: true, once: true },
)

watch(currentValue, () => {
  value.value = currentValue.value
})

/**
 * Функция выбора типа периода
 * @param id
 */
const selectHandler = ({ id }) => {
  currentPeriod.value = id
}

/** Вычисляем форматы времени **/
const formats = computed(() => {
  switch (props.type) {
    case 'date':
      return 'dd.MM.yyyy'
    case 'period':
    case 'range':
      return 'dd.MM.yyyy'
    case 'month':
      return 'LLLL'
    case 'year':
      return 'yyyy'
    default:
      return 'dd.MM.yyyy'
  }
})

/** Вычисляем аттрибуты времени **/
const bindProps = computed(() => {
  switch (props.type) {
    case 'period':
      return {
        range: {
          autoRange: periodsList[currentPeriod.value].rangeValue,
        },
      }
    case 'range':
      return { range: true }
    case 'month':
      return { monthPicker: true }
    case 'year':
      return { yearPicker: true }
    default:
      return {}
  }
})

/** Сбрасываем значение при смене */
watch(
  () => props.type,
  () => (currentValue.value = ''),
)
</script>

<template>
  <VueDatePicker
    ref="pickerRef"
    class="invent-type-field__date-picker"
    :class="[
      wrapperClass,
      {
        'invent-type-field__date-picker--error': mandatoryErrorVisible,
        'invent-type-field__date-picker--open': isPickerOpen,
      },
    ]"
    :data-label="label || undefined"
    v-model="currentValue"
    auto-apply
    teleport="body"
    :placeholder="dateInputPlaceholder"
    :loading="loading"
    :disabled="disabled"
    :model-type="formats"
    :formats="{ input: formats }"
    :time-config="{ enableTimePicker: false }"
    :year-range="[1950, 2100]"
    :locale="ru"
    @pointerdown="registerUserInteraction()"
    @open="handleOpen"
    @closed="onDatePickerClosed"
    v-bind="{ ...pickerAttrs, ...bindProps }"
  />

  <VMenu v-if="type === 'period'">
    <template v-slot:activator="{ props }">
      <VBtn append-icon="arrow_drop_down" v-bind="props">
        <VIcons type="date_range" />
      </VBtn>
    </template>

    <VList :items="Object.values(periodsList)" @click:select="selectHandler" />
  </VMenu>
</template>
